import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import type { DraftPresentation } from '../../contracts/approval-kinds';
import {
  applyFindReplace,
  applySpanEdit,
  findReplaceModeForTarget,
} from './apply-find-replace';
import { extractStreamingDraftBody } from './extract-streaming-draft-body';

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

const readFindReplaceMap = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const SPAN_OLD_KEY = 'old_string';
const SPAN_NEW_KEY = 'new_string';

const formatSpanEditMarkdown = (
  oldString: string,
  newString: string,
  replaceAll: boolean,
  intl: IntlShape
): string => {
  if (!oldString && !newString) return '';
  const beforeLabel = replaceAll
    ? intl.formatMessage({
        defaultMessage: 'Before (every occurrence):',
        id: 'Zhh/JAd9Yi',
      })
    : intl.formatMessage({
        defaultMessage: 'Before:',
        id: 'EgayzJoGku',
      });
  const afterLabel = intl.formatMessage({
    defaultMessage: 'After:',
    id: 'uB+72t2QW6',
  });
  const blocks: string[] = [];
  if (oldString) blocks.push(`**${beforeLabel}**\n${oldString}`);
  if (newString) blocks.push(`**${afterLabel}**\n${newString}`);
  return blocks.join('\n\n');
};

/** What a draft card shows and copies, from the tool input or its stream. */
export const useDraftPreview = ({
  toolInput,
  partialInput,
  draft,
  beforeText,
}: {
  toolInput: Record<string, unknown>;
  partialInput?: string;
  draft: DraftPresentation | null;
  beforeText?: string;
}) => {
  const intl = useIntl();
  const findReplace = draft?.find_replace_key
    ? readFindReplaceMap(toolInput[draft.find_replace_key])
    : null;
  const inputBody = draft ? readString(toolInput[draft.body_key]) : '';
  const streamedBody = draft
    ? extractStreamingDraftBody(partialInput, draft.body_key)
    : '';
  const spanOldString =
    readString(toolInput[SPAN_OLD_KEY]) ||
    extractStreamingDraftBody(partialInput, SPAN_OLD_KEY);
  const spanNewString =
    readString(toolInput[SPAN_NEW_KEY]) ||
    extractStreamingDraftBody(partialInput, SPAN_NEW_KEY);
  const spanEditMarkdown =
    !inputBody && !findReplace
      ? formatSpanEditMarkdown(
          spanOldString,
          spanNewString,
          toolInput.replace_all === true,
          intl
        )
      : '';

  const preview = useMemo(() => {
    if (!draft)
      return {
        body: streamedBody || inputBody || spanEditMarkdown,
        unmatched: [] as string[],
      };
    if (inputBody) return { body: inputBody, unmatched: [] as string[] };
    if (findReplace && beforeText) {
      const rewritten = applyFindReplace(
        beforeText,
        findReplace,
        findReplaceModeForTarget(draft.target)
      );
      return { body: rewritten.text, unmatched: rewritten.unmatched };
    }
    if (beforeText && spanOldString) {
      const rewritten = applySpanEdit(
        beforeText,
        spanOldString,
        spanNewString,
        toolInput.replace_all === true
      );
      if (rewritten.applied) {
        return { body: rewritten.text, unmatched: [] as string[] };
      }
    }
    return { body: streamedBody || spanEditMarkdown, unmatched: [] as string[] };
  }, [
    beforeText,
    draft,
    findReplace,
    inputBody,
    spanEditMarkdown,
    spanNewString,
    spanOldString,
    streamedBody,
    toolInput.replace_all,
  ]);

  const fallbackFindReplace = findReplace
    ? Object.entries(findReplace)
        .map(([find, replace]) => `${find} → ${String(replace)}`)
        .join('\n')
    : '';
  const displayBody = preview.body || fallbackFindReplace;
  const showingSpanPatch = Boolean(spanEditMarkdown) && displayBody === spanEditMarkdown;
  const comparison = showingSpanPatch
    ? { before: spanOldString, after: spanNewString }
    : undefined;

  const copyText = showingSpanPatch && spanNewString ? spanNewString : displayBody;
  return {
    inputBody,
    displayBody,
    unmatched: preview.unmatched,
    comparison,
    copyText,
  };
};
